import { Button } from "@attmos/flowstack-ui";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { NO_ORGANIZATION_MESSAGE, useLogout, useSession } from "@/api/auth";

export function RequireSession({ children }: { children: ReactNode }) {
  const { data: session, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center text-sm text-muted-foreground"
      >
        Loading…
      </div>
    );
  }

  if (!session) {
    // Cognito is the only auth path now that #154 deleted the pilot
    // form. Per Rupert PR-328 r1 #11: preserve search + hash so a
    // deep link like ``/projects?filter=mine#section`` round-trips
    // through sign-in instead of silently dropping query state.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/auth/cognito/sign-in" replace state={{ returnTo }} />;
  }

  if (!session.tenant_id) {
    return <NoOrganization />;
  }

  return <>{children}</>;
}

// fs#1206: a signed-in user with no organization would meet only 403
// ``no_organization`` inside the portal; say why instead. The org is
// stamped at sign-in, so once an administrator adds them the user must
// sign in again — and the portal's own Sign out (``UserMenu``) is out of
// reach here, so offer it (Rupert PR-2065 R-16/R-20).
function NoOrganization() {
  const logout = useLogout();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-muted px-4">
      <div
        role="alert"
        className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
      >
        {NO_ORGANIZATION_MESSAGE}
      </div>
      <Button variant="outline" disabled={logout.isPending} onClick={() => logout.mutate()}>
        {logout.isPending ? "Signing out…" : "Sign out"}
      </Button>
    </div>
  );
}
